import type {
  CreateTareProfileRequest,
  CreateUnitProfileRequest,
  TareProfileMode,
  UnitProfileTemplateKey,
} from '@crv/contracts';

import { deriveUnitProfileCapabilityPolicy } from './validation.js';

export interface StarterProfileDefaultsOptions {
  bagDefaultTareLb?: number;
}

export type StarterTareProfileDefinition = Omit<
  CreateTareProfileRequest,
  'companyId' | 'storeId' | 'locationId'
>;

export type StarterUnitProfileDefinition = Omit<
  CreateUnitProfileRequest,
  'companyId' | 'storeId' | 'locationId' | 'tareProfileId'
> & {
  tareProfileCode: string;
};

export interface StarterProfileDefaults {
  tareProfiles: StarterTareProfileDefinition[];
  unitProfiles: StarterUnitProfileDefinition[];
}

const TARE_NO_TARE = 'NO_TARE';
const TARE_BAG_DEFAULT = 'BAG_DEFAULT_TARE';
const TARE_MANUAL_REQUIRED = 'MANUAL_TARE_REQUIRED';
const TARE_FIXED_ASSET_MASTER = 'FIXED_ASSET_MASTER_TARE';

export function buildStarterUnitProfileDefaults(
  options?: StarterProfileDefaultsOptions,
): StarterProfileDefaults {
  const bagDefaultTareLb = options?.bagDefaultTareLb ?? 0;

  return {
    tareProfiles: [
      tareProfile({
        code: TARE_NO_TARE,
        displayName: 'No tare',
        description: 'Starter profile for units without a default tare.',
        mode: 'none',
        displayOrder: 10,
      }),
      tareProfile({
        code: TARE_BAG_DEFAULT,
        displayName: 'Bag default tare',
        description: 'Starter fixed tare for bag source units.',
        mode: 'fixed_default',
        defaultTareLb: bagDefaultTareLb,
        allowOperatorOverride: true,
        requireOperatorConfirmation: true,
        displayOrder: 20,
      }),
      tareProfile({
        code: TARE_MANUAL_REQUIRED,
        displayName: 'Manual tare required',
        description: 'Starter profile requiring an operator-entered tare.',
        mode: 'manual_required',
        allowOperatorOverride: true,
        requireOperatorConfirmation: true,
        displayOrder: 30,
      }),
      tareProfile({
        code: TARE_FIXED_ASSET_MASTER,
        displayName: 'Fixed asset master tare',
        description: 'Starter profile resolving tare from the fixed-ID asset master record.',
        mode: 'fixed_asset_master',
        displayOrder: 40,
      }),
    ],
    unitProfiles: [
      unitProfile({
        code: 'SOURCE_LOOSE',
        displayName: 'Loose source',
        description: 'Starter loose source material profile.',
        templateKey: 'source_loose',
        tareProfileCode: TARE_NO_TARE,
        displayOrder: 10,
      }),
      unitProfile({
        code: 'SOURCE_BAG',
        displayName: 'Bag source',
        description: 'Starter bag source material profile.',
        templateKey: 'source_bag',
        tareProfileCode: TARE_BAG_DEFAULT,
        displayOrder: 20,
      }),
      unitProfile({
        code: 'SOURCE_HEAVY_CONTAINER',
        displayName: 'Heavy container source',
        description: 'Starter generic heavy container source profile.',
        templateKey: 'source_heavy_container',
        tareProfileCode: TARE_MANUAL_REQUIRED,
        displayOrder: 30,
      }),
      unitProfile({
        code: 'FIXED_ID_SOURCE_HEAVY_CONTAINER',
        displayName: 'Fixed-ID source container',
        description: 'Starter fixed-ID heavy container source profile.',
        templateKey: 'fixed_id_source_heavy_container',
        tareProfileCode: TARE_FIXED_ASSET_MASTER,
        displayOrder: 40,
      }),
      unitProfile({
        code: 'STAGING_CONTAINER',
        displayName: 'Staging container',
        description: 'Starter staging profile for build-product workflows.',
        templateKey: 'staging_container',
        tareProfileCode: TARE_NO_TARE,
        displayOrder: 50,
      }),
      unitProfile({
        code: 'PRODUCT_BALE',
        displayName: 'Baled product',
        description: 'Starter bale product profile.',
        templateKey: 'product_bale',
        tareProfileCode: TARE_NO_TARE,
        displayOrder: 60,
      }),
      unitProfile({
        code: 'LEFTOVER_LOOSE',
        displayName: 'Loose leftover',
        description: 'Starter leftover material profile.',
        templateKey: 'leftover_loose',
        tareProfileCode: TARE_NO_TARE,
        displayOrder: 70,
      }),
    ],
  };
}

function tareProfile(
  input: StarterTareProfileDefinition & { mode: TareProfileMode },
): StarterTareProfileDefinition {
  return {
    allowOperatorOverride: false,
    requireOperatorConfirmation: false,
    isActive: true,
    ...input,
  };
}

function unitProfile(
  input: Omit<StarterUnitProfileDefinition, 'capabilityPolicy'> & {
    templateKey: UnitProfileTemplateKey;
  },
): StarterUnitProfileDefinition {
  return {
    isActive: true,
    capabilityPolicy: deriveUnitProfileCapabilityPolicy(input.templateKey),
    ...input,
  };
}
